/**
 * Counter Animation Module
 * 
 * Handles animated counters that count up from 0 to values specified in data-stat attributes.
 * Supports both number counters and percentage counters with intersection observer triggers.
 *
 * @package Lastag
 * @version 1.0.0
 */

class Counter {
    constructor(options = {}) {
        this.options = {
            threshold: 0.1,
            defaultDuration: '3s',
            ...options
        };
        this.observers = new Set();
        this.supportsAtProperty = this.checkAtPropertySupport();
    }

    checkAtPropertySupport() {
        const isFirefox = typeof navigator !== 'undefined' && navigator.userAgent.toLowerCase().includes('firefox');
        if (isFirefox) {
            return false;
        }

        const isSafari = typeof navigator !== 'undefined' && 
            navigator.userAgent.includes('Safari') && 
            !navigator.userAgent.includes('Chrome');
        if (isSafari) {
            return false;
        }

        if (typeof CSS !== 'undefined' && CSS.registerProperty) {
            try {
                CSS.registerProperty({
                    name: '--test-counter-animation',
                    syntax: '<integer>',
                    initialValue: '0',
                    inherits: false
                });
                
                const isChromium = typeof navigator !== 'undefined' && 
                    (navigator.userAgent.includes('Chrome') || 
                     navigator.userAgent.includes('Chromium') ||
                     navigator.userAgent.includes('Edge'));
                
                if (isChromium) {
                    return true;
                } else {
                    return false;
                }
            } catch (e) {
                return false;
            }
        }
        
        return false;
    }

    init() {
        this.initializeNumberCounters();
        this.initializePercentCounters();
    }

    initializeNumberCounters() {
        const numberCounters = document.querySelectorAll('.number-counter[data-stat]');
        numberCounters.forEach(counter => {
            this.setupCounter(counter, '--target');
        });
    }

    initializePercentCounters() {
        const percentCounters = document.querySelectorAll('.percent-counter[data-stat]');
        percentCounters.forEach(counter => {
            this.setupCounter(counter, '--percent-target');
        });
    }

    /**
     * Setup individual counter with intersection observer
     * @param {HTMLElement} counter - The counter element
     * @param {string} cssProperty - The CSS custom property to set
     */
    setupCounter(counter, cssProperty) {
        const targetValue = parseInt(counter.getAttribute('data-stat'), 10);
        const duration = counter.getAttribute('data-duration') || this.options.defaultDuration;

        if (isNaN(targetValue)) {
            console.warn('Counter element has invalid data-stat value:', counter);
            return;
        }

        counter.style.setProperty(cssProperty, targetValue);
        counter.style.setProperty('--duration', duration);

        const observer = new IntersectionObserver((entries) => {
            entries.forEach(entry => {
                if (entry.isIntersecting && !entry.target.classList.contains('animate')) {
                    this.animateCounter(entry.target);
                    observer.unobserve(entry.target);
                    this.observers.delete(observer);
                }
            });
        }, { threshold: this.options.threshold });

        observer.observe(counter);
        this.observers.add(observer);
    }

    /**
     * Start the counter animation
     * @param {HTMLElement} counter - The counter element to animate
     */
    animateCounter(counter) {
        counter.classList.add('animate');

        counter.dispatchEvent(new CustomEvent('counterStart', {
            detail: { 
                target: parseInt(counter.getAttribute('data-stat'), 10),
                element: counter
            }
        }));

        if (!this.supportsAtProperty) {
            this.animateCounterFallback(counter);
        } else {
            counter.addEventListener('animationend', () => {
                counter.dispatchEvent(new CustomEvent('counterEnd', {
                    detail: { 
                        target: parseInt(counter.getAttribute('data-stat'), 10),
                        element: counter
                    }
                }));
            }, { once: true });
        }
    }

    animateCounterFallback(counter) {
        const targetValue = parseInt(counter.getAttribute('data-stat'), 10);
        const duration = this.parseDuration(counter.getAttribute('data-duration') || this.options.defaultDuration);
        const isPercent = counter.classList.contains('percent-counter');
        let startTime = null;
        const startValue = 0;

        const animate = (currentTime) => {
            if (!startTime) startTime = currentTime;
            const elapsed = currentTime - startTime;
            const progress = Math.min(elapsed / duration, 1);
            const easedProgress = progress < 0.5 
                ? 2 * progress * progress 
                : 1 - Math.pow(-2 * progress + 2, 2) / 2;
            
            const currentValue = Math.round(startValue + (targetValue - startValue) * easedProgress);

            if (isPercent) {
                counter.style.setProperty('--percent-num', currentValue);
            } else {
                counter.style.setProperty('--num', currentValue);
            }
            
            if (progress < 1) {
                requestAnimationFrame(animate);
            } else {
                counter.dispatchEvent(new CustomEvent('counterEnd', {
                    detail: { 
                        target: targetValue,
                        element: counter
                    }
                }));
            }
        };

        requestAnimationFrame(animate);
    }

    parseDuration(duration) {
        const match = duration.match(/^(\d+(?:\.\d+)?)(s|ms)$/);
        if (!match) return 3000
        
        const value = parseFloat(match[1]);
        const unit = match[2];
        
        return unit === 's' ? value * 1000 : value;
    }

    /**
     * Manually trigger a counter animation (useful for testing or manual triggers)
     * @param {HTMLElement|string} counterElement - Counter element or selector
     */
    triggerCounter(counterElement) {
        const element = typeof counterElement === 'string' 
            ? document.querySelector(counterElement) 
            : counterElement;

        if (element && !element.classList.contains('animate')) {
            this.animateCounter(element);
        }
    }

    /**
     * Reset a counter to its initial state
     * @param {HTMLElement|string} counterElement - Counter element or selector
     */
    resetCounter(counterElement) {
        const element = typeof counterElement === 'string' 
            ? document.querySelector(counterElement) 
            : counterElement;

        if (element) {
            element.classList.remove('animate');
            element.offsetHeight;
        }
    }

    /**
     * Reset all counters
     */
    resetAllCounters() {
        const allCounters = document.querySelectorAll('.number-counter, .percent-counter');
        allCounters.forEach(counter => this.resetCounter(counter));
    }

    /**
     * Cleanup observers (useful when component is destroyed)
     */
    destroy() {
        this.observers.forEach(observer => observer.disconnect());
        this.observers.clear();
    }
}

/**
 * Default counter instance
 */
const counterInstance = new Counter();

/**
 * Initialize counters when DOM is ready
 */
function initializeCounters(options = {}) {
    if (options && Object.keys(options).length > 0) {
        const customCounter = new Counter(options);
        customCounter.init();
        return customCounter;
    }
    
    counterInstance.init();
    return counterInstance;
}

export { Counter, initializeCounters };

if (typeof window !== 'undefined') {
    window.LastagCounter = Counter;
    window.initializeCounters = initializeCounters;
}
